import { Address } from "shared/utils/fieldsHelper/companyFields";
import { Status, StatusDropdown } from "components";
import FormattedAddress from "components/Atoms/FormattedAddress";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import LocalTimeRow from "components/DetailPage/LocalTimeRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UploadAvatar from "components/UploadAvatar/UploadAvatar";
import { AddressInnerForm } from "components/ValidatedFields/AddressInnerForm";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import ContactEmailRow from "contact/detail/components/ContactEmailRow";
import { ContactName } from "contact/detail/components/ContactName";
import { EmailWithBlacklist } from "contact/detail/components/EmailWithBlacklist";
import { GenderRadio } from "contact/detail/components/GenderRadio";
import { getInstruction, ContactFormState, ContactFieldMetaData } from "shared/utils/fieldsHelper/contactFields";
import React from "react";
import { Message } from "semantic-ui-react";
import { getCultureFromLcid } from "utilities/enumutils";
import { getGenderName } from "utilities/genderUtils";

export const contactMainSection = (
  formState: ContactFormState,
  fieldMetaData: ContactFieldMetaData,
  profileImageUrl: string | undefined,
  error: string | undefined,
  onSave: (file: File) => void
): FormSection => {
  return {
    key: "contactMainDetails",
    type: "regular",
    title: RESX.Contact.resxContact,
    fields: [{
      ...fieldMetaData.deleted.fields(),
      key: "deleted",
      label: RESX.File.Deleted,
      render: () => <DeletedRow />,
    }, {
      ...fieldMetaData.isValidated.fields(),
      key: "isValidated",
      label: RESX.Planning.ExternalInput,
      render: () => <NotValidatedRow
        canApprove={!fieldMetaData.isValidated.fields().editDisabled}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Contact.Validate({
          contactId: formState.contactId
        })}
      />,
    }, {
      ...fieldMetaData.name.fields(),
      key: "name",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{[formState.firstName, formState.insertion, formState.lastName].filter(Boolean).join(" ")}</div>,
      inPageEdit: {
        initialValue: [formState.firstName, formState.insertion, formState.lastName],
        render: ({ value, onChange, submit, cancel }) =>
          <ContactName
            focusOnMount
            firstName={value[0]}
            insertion={value[1]}
            lastName={value[2]}
            onChange={(firstName, insertion, lastName) => onChange([firstName, insertion, lastName])}
            onEnter={submit}
            onEsc={cancel}
            width={130}
          />,
        submitChange: ([firstName, insertion, lastName]: string[]) => {
          const instruction = getInstruction(formState, {
            name: {
              firstName: firstName,
              insertion: insertion,
              lastName: lastName,
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction)
        },
        type: "confirmOrCancelButton"
      }
    }, {
      ...fieldMetaData.email.fields(),
      key: "emailAddresses",
      label: RESX.GeneralLabels.Email,
      render: () => <ContactEmailRow
        emailAddresses={formState.emailAddresses}
        emailBlacklisted={formState.isEmailBlacklisted}
      />,
      inPageEdit: {
        initialValue: [formState.emailAddresses, formState.isEmailBlacklisted],
        render: ({ value, onChange, submit, cancel }) => <EmailWithBlacklist
          value={value}
          onChange={v => onChange(v)}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        submitChange: (value: [string, boolean]) => {
          const instruction = getInstruction(formState, { emailAddresses: value[0], emailBlacklisted: value[1] });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    },
    {
      ...fieldMetaData.mobileNumbers.fields(),
      key: "mobileNumbers",
      label: RESX.Contact.Mobile,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.mobileNumbers)} />,
      inPageEdit: {
        initialValue: formState.mobileNumbers,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { mobileNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      ...fieldMetaData.landlineNumbers.fields(),
      key: "landline",
      label: RESX.ContactDetails.PhoneNumber,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.landlineNumbers)} />,
      inPageEdit: {
        initialValue: formState.landlineNumbers,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { landlineNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      ...fieldMetaData.culture.fields(),
      key: "language",
      label: RESX.Language.resxLanguage,
      render: () => <CultureLabel culture={getCultureFromLcid(formState.culture)} />,
      inPageEdit: {
        initialValue: formState.culture,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <CulturesTemplateDropdown
            value={value}
            onChange={c => onChange(c, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.LCID) => {
          const instruction = getInstruction(formState, { culture: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      ...fieldMetaData.gender.fields(),
      key: "gender",
      label: RESX.Contact.Gender,
      render: () => getGenderName(formState.gender),
      inPageEdit: {
        initialValue: formState.gender,
        render: ({ value, onChange }) => <div className="main-section-template-fields-padding">
          <GenderRadio
            value={value}
            onChange={v => onChange(v, true)}
          />
        </div>,
        type: "custom",
        submitChange: (value: server.dto.Genders) => {
          const instruction = getInstruction(formState, { gender: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }

      }
    }, {
      ...fieldMetaData.position.fields(),
      key: "position",
      label: RESX.Contact.Position,
      render: () => <div>{formState.position}</div>,
      inPageEdit: {
        initialValue: formState.position,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxPositionLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { position: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      ...fieldMetaData.department.fields(),
      key: "department",
      label: RESX.Contact.Department,
      render: () => <div>{formState.department}</div>,
      inPageEdit: {
        initialValue: formState.department,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxDepartmentLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { department: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      ...fieldMetaData.tags.fields(),
      key: "tags",
      label: RESX.Tag.Tags,
      render: () => <TagList tags={formState.tags} version="long" />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <TagsSelect
            mainArea="contact"
            value={value}
            onChange={onChange}
            inPageEditing="edit"
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Contact.UpdateTags({ itemId: formState.contactId, tagsIds: tags.map(tag => tag.tagId) });
        }
      }
    }, {
      key: "profileImage",
      label: RESX.ESignLanding.btnImage,
      render: () => <div className="flex-column gap">
        <UploadAvatar avatarUrl={profileImageUrl} onSave={onSave} />
        {error && <Message color="red">{error}</Message>}
      </div>
    }, {
      ...fieldMetaData.salutation.fields(),
      key: "salutation",
      label: RESX.Contact.Salutation,
      render: () => formState.salutation,
      inPageEdit: {
        initialValue: formState.salutation,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxSalutationLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { salutation: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      ...fieldMetaData.attn.fields(),
      key: "attn",
      label: RESX.Contact.AttnLineOnEnvelope,
      render: () => formState.attn,
      inPageEdit: {
        initialValue: formState.attn,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxAttnLength}
            placeholder={RESX.Contact.AttnLineOnEnvelopeDesc}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(formState, { attn: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      ...fieldMetaData.address.fields(),
      key: "homeAddress",
      label: RESX.Contact.HomeAddress,
      render: () => <FormattedAddress address={formState.homeAddress.dto} />,
      inPageEdit: {
        initialValue: formState.homeAddress,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <AddressInnerForm
            address={value}
            onChange={onChange}
            submit={submit}
            isSaving={isSaving}
          />
        ),
        type: "custom",
        submitChange: (value: Address) => {
          const instruction = getInstruction(formState, {
            address: {
              addressLine1: value.addressLine1,
              addressLine2: value.addressLine2,
              postalCode: value.postalCode,
              city: value.city,
              stateId: value.state.key
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }

    }, {
      ...fieldMetaData.localTime.fields(),
      key: "localTime",
      label: RESX.DateTime.LocalTime,
      render: () => <LocalTimeRow ianaTimeZoneId={formState.ianaTimeZoneId} />,
    }, {
      ...fieldMetaData.status.fields(),
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={formState.status} />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="contact"
            clientId={formState.clientId}
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(formState, { statusId: value.statusId });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      ...fieldMetaData.owner.fields(),
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={formState.owner} />,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: formState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(formState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }]
  }
};